import React from "react";
import {
  Modal,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import { setClearSaveJsonStatus } from "../../redux/slices/outputJson";

interface TaskStatusModalProps {
  open: boolean;
  onClose: () => void;
  taskStatusData: {
    summary_docx_url?: string;
    status: "PENDING" | "SUCCESS" | "FAILURE";
    summary?: Record<string, string | number> | string | null;
    url?: string | null;
    message?: string | null;
    error?: string | null;
    result?: { reasoning?: string | null; error?: string | null } | null;
    data?: {
      detail?: string | null;
      result?: { reasoning?: string | null; error?: string | null } | null;
    } | null;
  } | null;
  completedTaskExported?: boolean;
  testAttempts?: boolean;
  taskByExperts?: boolean;
  fileUpload?: boolean;
  scriptExecution?: boolean;
}

const DEFAULT_SCRIPT_EXECUTION_ERROR_MESSAGE = "Script execution failed. Please try again later.";

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const decodeHtmlEntities = (value: string) => {
  if (typeof window === "undefined") return value;

  const textArea = window.document.createElement("textarea");
  textArea.innerHTML = value;
  return textArea.value;
};

const extractHtmlErrorMessage = (value: string) => {
  const headingMatch = value.match(/<h1[^>]*>(.*?)<\/h1>/i);
  const titleMatch = value.match(/<title[^>]*>(.*?)<\/title>/i);
  const footerMatch = value.match(/<hr\s*\/?><center>(.*?)<\/center>/i);

  const primaryMessage = normalizeWhitespace(
    decodeHtmlEntities(
      (headingMatch?.[1] || titleMatch?.[1] || value.replace(/<[^>]+>/g, " ")).replace(/<[^>]+>/g, " ")
    )
  );
  const footerMessage = footerMatch
    ? normalizeWhitespace(decodeHtmlEntities(footerMatch[1].replace(/<[^>]+>/g, " ")))
    : "";

  if (!footerMessage || primaryMessage.includes(footerMessage)) {
    return primaryMessage;
  }

  return `${primaryMessage} (${footerMessage})`;
};

const extractErrorMessage = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = extractErrorMessage(item);
      if (message) return message;
    }
    return null;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (parsedValue !== trimmedValue) {
        const parsedMessage = extractErrorMessage(parsedValue);
        if (parsedMessage) return parsedMessage;
      }
    } catch {
      // Fall back to raw string parsing.
    }

    const htmlStartIndex = trimmedValue.search(/<html[\s>]/i);
    if (htmlStartIndex >= 0) {
      const prefix = normalizeWhitespace(trimmedValue.slice(0, htmlStartIndex).replace(/[:\s]+$/, ""));
      const htmlMessage = extractHtmlErrorMessage(trimmedValue.slice(htmlStartIndex));
      return prefix ? `${prefix}: ${htmlMessage}` : htmlMessage;
    }

    if (/<[^>]+>/.test(trimmedValue)) {
      return extractHtmlErrorMessage(trimmedValue);
    }

    return normalizeWhitespace(trimmedValue);
  }

  if (typeof value === "object") {
    const objectValue = value as Record<string, unknown>;
    const directKeys = ["detail", "error", "message", "reasoning", "reason", "failure_reason", "failureReason"];
    for (const key of directKeys) {
      const message = extractErrorMessage(objectValue[key]);
      if (message) return message;
    }

    const nestedKeys = ["result", "data"];
    for (const key of nestedKeys) {
      const message = extractErrorMessage(objectValue[key]);
      if (message) return message;
    }
  }

  return typeof value === "number" || typeof value === "boolean" ? String(value) : null;
};

const TaskStatusModal: React.FC<TaskStatusModalProps> = ({
  open,
  onClose,
  taskStatusData,
  completedTaskExported,
  testAttempts,
  taskByExperts,
  fileUpload,
  scriptExecution,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { taskStatusOutputJsonResponse } = useSelector((state: RootState) => state.outputJson)

  const handleGoToTaskPage = () => {
    navigate(`/project/${id}?tab=task`);
    onClose();
    dispatch(setClearSaveJsonStatus({}));
  };

  const handleGoToAttemptsPage = () => {
    navigate(`/project/${id}?tab=test&testTab=attempts`);
    onClose();
    dispatch(setClearSaveJsonStatus({}));
  }

  const handleGoToAnalyticsPage = () => {
    navigate(`/project/${id}?tab=analytics`);
    onClose();
    dispatch(setClearSaveJsonStatus({}));
  };

  const handleGenerateJsonAgain = () => {
    onClose();
    window.location.reload();
  };

  const capitalizeWords = (str: string) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const scriptExecutionErrorMessage = extractErrorMessage(taskStatusData?.data?.detail)
    ?? extractErrorMessage(taskStatusData?.result?.reasoning)
    ?? extractErrorMessage(taskStatusData?.result?.error)
    ?? extractErrorMessage(taskStatusData?.data?.result?.reasoning)
    ?? extractErrorMessage(taskStatusData?.data?.result?.error)
    ?? extractErrorMessage(taskStatusData?.error)
    ?? extractErrorMessage(taskStatusData?.message)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.data?.detail)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.data?.result?.reasoning)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.data?.result?.error)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.error)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.message)
    ?? DEFAULT_SCRIPT_EXECUTION_ERROR_MESSAGE;

  const genericFailureMessage = extractErrorMessage(taskStatusOutputJsonResponse?.message)
    ?? extractErrorMessage(taskStatusOutputJsonResponse?.error)
    ?? "Please try again or contact support.";

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "#fefefe",
          borderRadius: 4,
          boxShadow: 24,
          p: 5,
          textAlign: "center",
          outline: "none",
        }}
      >
        {taskStatusData?.status !== "PENDING" &&
          !completedTaskExported &&
          !taskByExperts && !testAttempts && (
            <IconButton
              onClick={scriptExecution ? onClose : handleGenerateJsonAgain}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        {taskStatusData?.status !== "PENDING" &&
          (completedTaskExported || taskByExperts) && (
            <IconButton
              onClick={
                completedTaskExported
                  ? handleGoToTaskPage :
                  testAttempts ? handleGoToAttemptsPage
                  : handleGoToAnalyticsPage
              }
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        {taskStatusData?.status === "PENDING" && (
          <>
            <HourglassTopIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Processing...
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {completedTaskExported || testAttempts
                ? "We are extracting your data, hang tight!"
                : taskByExperts
                  ? "We are extracting your data, hang tight!"
                  : fileUpload ? "We are processing your file upload, hang tight!"
                    : "We are extracting your output JSON, hang tight!"}
            </Typography>
            <CircularProgress color="primary" />
          </>
        )}
        {fileUpload && taskStatusData?.status === "SUCCESS" ? (
          <>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 48, mb: 1 }}
            />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {scriptExecution
                ? "Script Executed Successfully!"
                : completedTaskExported || testAttempts
                  ? "Data Exported Successfully!"
                  : taskByExperts
                    ? "Data Exported Successfully!"
                    : "File Uploaded Successfully!"}
            </Typography>
            {!completedTaskExported && !testAttempts && !taskByExperts && !fileUpload && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                Here’s a quick summary of the extracted content:
              </Typography>
            )}
            <Chip
              label={
                scriptExecution
                  ? "Ok"
                  : taskByExperts ? "Go to Analytics Page" : testAttempts ? "Go to Attempts Page" : "Go to Task Page"
              }
              color="primary"
              clickable
              onClick={
                scriptExecution
                  ? onClose
                  : taskByExperts ? handleGoToAnalyticsPage : testAttempts ? handleGoToAttemptsPage : handleGoToTaskPage
              }
            />
          </>)
          : taskStatusData?.status === "SUCCESS" && (taskStatusData?.url) && (
            <>
              <CheckCircleOutlineIcon
                color="success"
                sx={{ fontSize: 48, mb: 1 }}
              />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {completedTaskExported || testAttempts
                  ? "Data Exported Successfully!"
                  : taskByExperts
                    ? "Data Exported Successfully!"
                    : fileUpload ? "File Uploaded Successfully!"
                      : "Output JSON Extracted Successfully!"}
              </Typography>
              {!completedTaskExported && !testAttempts && !taskByExperts && !fileUpload && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Here’s a quick summary of the extracted content:
                </Typography>
              )}

              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap={2}
                mt={2}
                justifyContent="center"
              >
                {Object.entries(taskStatusData?.summary ?? {}).map(
                  ([key, value]) => (
                    <Chip
                      key={key}
                      label={`${capitalizeWords(
                        key.replace(/_/g, " ")
                      )}: ${value}`}
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: 13 }}
                    />
                  )
                )}
              </Box>
              <Box mt={3}>
                {taskStatusData?.url && (
                  <a
                    href={taskStatusData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Chip
                      label={
                        completedTaskExported || taskByExperts || testAttempts
                          ? "Download Exported File"
                          : "View Exported File"
                      }
                      color="success"
                      variant="filled"
                      clickable
                      sx={{ marginRight: "10px" }}
                    />
                  </a>
                )}
                <Chip
                  label={
                    scriptExecution
                   ? "Ok"
                   : taskByExperts ? "Go to Analytics Page" : testAttempts ? "Go to Attempts Page" : "Go to Task Page"
                  }
                  color="primary"
                  clickable
                  onClick={
                    scriptExecution
                   ? onClose
                   : taskByExperts ? handleGoToAnalyticsPage : testAttempts ? handleGoToAttemptsPage : handleGoToTaskPage
                  }
                />
                {taskStatusData?.summary_docx_url && (
                  <a
                    href={taskStatusData.summary_docx_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Chip
                      label={"Download Summary"}
                      color="success"
                      variant="filled"
                      clickable
                      sx={{ marginRight: "10px", marginTop: "10px" }}
                    />
                  </a>
                )}
              </Box>
              {!completedTaskExported && !taskByExperts && !fileUpload && !testAttempts && (
                <Chip
                  onClick={handleGenerateJsonAgain}
                  sx={{ marginRight: "10px", mt: 2, fontSize: 13 }}
                  variant="outlined"
                  clickable
                  color="primary"
                  label="Regenerate JSON"
                />
              )}
            </>
          )}
        {fileUpload && taskStatusData?.status === "FAILURE" ? (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" color="error" fontWeight="bold" gutterBottom>
              {scriptExecution ? "Script Execution Failed" : "Something went wrong"}
            </Typography>
            {scriptExecution ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", mt: 0.5 }}>
                  {scriptExecutionErrorMessage}
                </Typography>
                {taskStatusData?.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {typeof taskStatusData.summary === "string"
                      ? taskStatusData.summary
                      : JSON.stringify(taskStatusData.summary)}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Please try again or contact support.
              </Typography>
            )}
            <Box mt={3}>
              <Chip
                label={
                  scriptExecution
                    ? "Ok"
                    : taskByExperts ? "Go to Analytics Page" : testAttempts ? 'Go to Attempts Page' : "Go to Task Page"
                }
                color="primary"
                clickable
                onClick={
                  scriptExecution
                    ? onClose
                    : taskByExperts ? handleGoToAnalyticsPage : testAttempts ? handleGoToAttemptsPage : handleGoToTaskPage
                }
              />
            </Box>
            {!completedTaskExported && !taskByExperts && !fileUpload && !testAttempts && (
              <Chip
                onClick={handleGenerateJsonAgain}
                sx={{ marginRight: "10px", mt: 2, fontSize: 13 }}
                variant="outlined"
                clickable
                color="primary"
                label="Regenerate JSON"
              />
            )}
          </>) :
          (taskStatusData?.status === "FAILURE" ||
            (!fileUpload && taskStatusData?.status === "SUCCESS" &&
              taskStatusData?.url === null)) && (
            <>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
              <Typography
                variant="h5"
                color="error"
                fontWeight="bold"
                gutterBottom
              >
                Something went wrong
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {genericFailureMessage}
              </Typography>
              <Box mt={3}>
                <Chip
                  label={
                    taskByExperts ? "Go to Analytics Page" : testAttempts ? 'Go to Attempts Page' :"Go to Task Page"
                  }
                  color="primary"
                  clickable
                  onClick={
                    taskByExperts ? handleGoToAnalyticsPage : testAttempts ? handleGoToAttemptsPage : handleGoToTaskPage
                  }
                />
              </Box>
              {!completedTaskExported && !taskByExperts && !fileUpload && !testAttempts && (
                <Chip
                  onClick={handleGenerateJsonAgain}
                  sx={{ marginRight: "10px", mt: 2, fontSize: 13 }}
                  variant="outlined"
                  clickable
                  color="primary"
                  label="Regenerate JSON"
                />
              )}
            </>
          )}
      </Box>
    </Modal>
  );
};

export default TaskStatusModal;
